import {ETIQUETA_SEVERIDAD, ETIQUETA_SEVERIDAD_EN, type AlertaDeCalidad, type Severidad} from '@cnc/contracts';
import {useAgenteDeCalidad, useMarcarAlertaVista, useRevisarAhora} from '../api/calidad';
import {et, localeActual, tr} from '../i18n/idioma';
import {fechaHora, numero} from '../utilidades/formato';
import {AYUDA_CALIDAD} from './ayuda-general';
import {NotaDeAyuda} from './nota-de-ayuda';
import {usePermisos} from './sesion-contexto';

// "07:15" de una fecha ISO, en la hora local (24 horas, como fechaHora).
const horaCorta = (iso: string) =>
  new Date(iso).toLocaleTimeString(localeActual(), {hour: '2-digit', minute: '2-digit', hourCycle: 'h23'});

const TONO: Record<Severidad, string> = {
  error: 'border-alerta/40 bg-red-50 text-alerta',
  warning: 'border-dorado/60 bg-dorado-claro text-tinta',
  info: 'border-linea bg-papel text-apagado',
};

// Agente de calidad (decision 0023): revisa los datos solo, cada cierto
// tiempo, y deja una alerta por cada revision con casos. "Nueva" = nadie la ha
// visto o sus casos aumentaron despues de verla.
export function AgenteDeCalidad({onAbrir}: {onAbrir: (clave: string) => void}) {
  const agente = useAgenteDeCalidad();
  const revisar = useRevisarAhora();
  const marcar = useMarcarAlertaVista();
  const permisos = usePermisos();
  const severidades = et(ETIQUETA_SEVERIDAD, ETIQUETA_SEVERIDAD_EN);
  const x = agente.data;
  const abiertas = x?.alertas.filter(a => a.estado === 'abierta') ?? [];
  const resueltas = x?.alertas.filter(a => a.estado === 'resuelta') ?? [];

  return (
    <div className="rounded-lg border border-linea bg-white p-4" data-prueba="agente-de-calidad">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-medium">
            {tr('Agente de calidad', 'Quality agent')}
            <NotaDeAyuda texto={AYUDA_CALIDAD.agente} />
            {x && x.nuevas > 0 && (
              <span className="ml-2 rounded-full bg-alerta px-2 py-0.5 text-xs text-white">
                {tr(`${x.nuevas} nuevas`, `${x.nuevas} new`)}
              </span>
            )}
          </h2>
          <p className="text-xs text-apagado">
            {x?.cadaMinutos
              ? tr(
                  `Revisa los datos solo cada ${x.cadaMinutos} minutos y avisa lo que no se puede hacer o hay que revisar.`,
                  `Checks the data on its own every ${x.cadaMinutos} minutes and flags what cannot be done or needs review.`,
                )
              : tr(
                  'La revisión automática está apagada en este equipo; se puede correr con "Revisar ahora".',
                  'Automatic checking is off on this machine; it can be run with "Check now".',
                )}
            {x?.ultimaCorrida &&
              tr(
                ` Última revisión: ${fechaHora(x.ultimaCorrida.inicio)}.`,
                ` Last check: ${fechaHora(x.ultimaCorrida.inicio)}.`,
              )}
          </p>
          {x?.ultimaCorrida?.error && (
            <p className="text-xs text-alerta">
              {tr('La última revisión tuvo errores: ', 'The last check had errors: ')}
              {x.ultimaCorrida.error}
            </p>
          )}
        </div>
        {permisos.editar && (
          <div className="flex flex-wrap gap-2 text-sm">
            {x && x.nuevas > 0 && (
              <button
                type="button"
                disabled={marcar.isPending}
                onClick={() => marcar.mutate('todas')}
                className="rounded-md border border-linea bg-white px-3 py-1.5 hover:bg-papel disabled:opacity-50"
              >
                {tr('✓ Marcar todas como vistas', '✓ Mark all as seen')}
              </button>
            )}
            <button
              type="button"
              disabled={revisar.isPending}
              onClick={() => revisar.mutate()}
              className="rounded-md bg-verde-oscuro px-3 py-1.5 text-white disabled:opacity-50"
            >
              {revisar.isPending ? tr('Revisando…', 'Checking…') : tr('↻ Revisar ahora', '↻ Check now')}
            </button>
          </div>
        )}
      </div>
      {agente.isError && <p className="mt-2 text-sm text-alerta">{agente.error.message}</p>}
      {revisar.isError && <p className="mt-2 text-sm text-alerta">{revisar.error.message}</p>}
      {x && abiertas.length === 0 && (
        <p className="mt-3 text-sm text-apagado">
          {x.ultimaCorrida
            ? tr('Sin alertas abiertas: todo en orden.', 'No open alerts: all good.')
            : tr('El agente todavía no ha revisado los datos.', 'The agent has not checked the data yet.')}
        </p>
      )}
      {abiertas.length > 0 && (
        <ul className="mt-3 divide-y divide-linea/70" data-prueba="alertas-de-calidad">
          {abiertas.map(a => (
            <FilaDeAlerta
              key={a.id}
              a={a}
              severidad={severidades[a.severidad]}
              onAbrir={() => onAbrir(a.clave)}
              onVista={permisos.editar && a.nueva ? () => marcar.mutate(a.id) : undefined}
            />
          ))}
        </ul>
      )}
      {resueltas.length > 0 && (
        <details className="mt-3 text-sm">
          <summary className="cursor-pointer text-apagado">
            {tr(
              `Resueltas en los últimos 7 días (${resueltas.length})`,
              `Resolved in the last 7 days (${resueltas.length})`,
            )}
          </summary>
          <ul className="mt-1 space-y-1">
            {resueltas.map(a => (
              <li key={a.id} className="text-apagado">
                ✓ {a.titulo} · {fechaHora(a.resueltaEl)}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function FilaDeAlerta({
  a,
  severidad,
  onAbrir,
  onVista,
}: {
  a: AlertaDeCalidad;
  severidad: string;
  onAbrir: () => void;
  onVista?: () => void;
}) {
  const cambio = a.casosAnteriores !== null ? a.casos - a.casosAnteriores : null;
  return (
    <li className="flex flex-wrap items-start gap-3 py-2 text-sm" data-alerta={a.clave}>
      <span className={`rounded-full border px-2 py-0.5 text-xs ${TONO[a.severidad]}`}>{severidad}</span>
      <div className="min-w-0 flex-1">
        <button type="button" onClick={onAbrir} className="text-left font-medium text-verde hover:underline">
          {a.nueva && <span className="mr-1 text-alerta">●</span>}
          {a.titulo}
        </button>
        <div className="text-xs text-apagado">
          {/* Cuenta de la ultima revision del agente; la tarjeta de la revision cuenta lo de ahora. */}
          {tr(
            `${numero(a.casos)} ${a.casos === 1 ? 'caso' : 'casos'} en la revisión de las ${horaCorta(a.ultimaRevision)}`,
            `${numero(a.casos)} ${a.casos === 1 ? 'case' : 'cases'} at the ${horaCorta(a.ultimaRevision)} check`,
          )}
          {cambio !== null && cambio !== 0 && (
            <span className={cambio > 0 ? 'text-alerta' : ''}>
              {' '}
              ({cambio > 0 ? '+' : ''}
              {numero(cambio)} {tr('desde la revisión anterior', 'since the previous check')})
            </span>
          )}
          {a.ejemplos.length > 0 && ` · ${tr('por ejemplo', 'e.g.')} ${a.ejemplos.join(', ')}`}
        </div>
        <div className="text-xs text-apagado">
          {tr(`Desde ${fechaHora(a.abiertaEl)}`, `Since ${fechaHora(a.abiertaEl)}`)}
          {a.vistaEl &&
            !a.nueva &&
            tr(
              ` · vista por ${a.vistaPor ?? 'alguien'} el ${fechaHora(a.vistaEl)}`,
              ` · seen by ${a.vistaPor ?? 'someone'} on ${fechaHora(a.vistaEl)}`,
            )}
        </div>
      </div>
      {onVista && (
        <button
          type="button"
          onClick={onVista}
          className="rounded-md border border-linea px-2 py-0.5 text-xs hover:bg-papel"
          aria-label={tr(`Marcar como vista: ${a.titulo}`, `Mark as seen: ${a.titulo}`)}
        >
          {tr('✓ Vista', '✓ Seen')}
        </button>
      )}
    </li>
  );
}
